import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ChevronRight, Link2, LockKeyhole, ShieldCheck, User } from "lucide-react";
import { useApiClient, useAuth } from "../../providers/AuthProvider";

// --- Types ---

type SecurityResponse = {
  twoFactorEnabled: boolean;
};

type TwoFactorSetup = {
  manualEntryKey: string;
  otpauthUrl: string;
  qrCodeDataUrl: string;
};

type SetupResponse = {
  setupToken: string;
  setup: TwoFactorSetup;
};

type PasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type DisableTwoFactorFormData = {
  currentPassword: string;
  code: string;
};

type IndyConnection =
  | { connected: false }
  | {
      connected: true;
      provider: string;
      email: string | null;
      clientName: string | null;
      expiresAt: string | null;
      updatedAt: string;
    };

type IndyConnectionResponse = {
  connection: IndyConnection;
};

type IndyLinkFormData = {
  email: string;
  password: string;
};

const formatExpiry = (value: string | null): string | null => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

// --- Change Password Dialog ---

type ChangePasswordDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ChangePasswordDialog = ({ isOpen, onClose }: ChangePasswordDialogProps) => {
  const apiClient = useApiClient();
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<PasswordFormData>({
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  const handleClose = () => {
    reset();
    setSuccess(false);
    onClose();
  };

  const onSubmit = async (data: PasswordFormData) => {
    if (data.newPassword !== data.confirmPassword) {
      setError("confirmPassword", { message: "Passwords do not match" });
      return;
    }

    try {
      await apiClient.post("/api/auth/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      setSuccess(true);
      reset();
    } catch (err: any) {
      setError("root", { message: err.message });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Change Password</h2>

        {success && (
          <div className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded text-emerald-300 text-sm">
            Password updated successfully.
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.root && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
              {errors.root.message}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              {...register("currentPassword", { required: "Current password is required" })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.currentPassword && <p className="mt-1 text-sm text-red-400">{errors.currentPassword.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              {...register("newPassword", {
                required: "New password is required",
                minLength: { value: 8, message: "Password must be at least 8 characters" },
              })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.newPassword && <p className="mt-1 text-sm text-red-400">{errors.newPassword.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              {...register("confirmPassword", { required: "Please confirm your new password" })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50"
            >
              {isSubmitting ? "Saving..." : "Change Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Two-Factor Setup Dialog ---

type TwoFactorSetupDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const TwoFactorSetupDialog = ({ isOpen, onClose, onSuccess }: TwoFactorSetupDialogProps) => {
  const apiClient = useApiClient();
  const { refreshAuth, completeTwoFactorSetup } = useAuth();
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [setupDetails, setSetupDetails] = useState<TwoFactorSetup | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<{ code: string }>({ defaultValues: { code: "" } });

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setLoadError(null);
    reset();

    apiClient
      .post<SetupResponse>("/api/auth/2fa/setup", {})
      .then((data) => {
        setSetupToken(data.setupToken);
        setSetupDetails(data.setup);
      })
      .catch((err: any) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleClose = () => {
    setSetupToken(null);
    setSetupDetails(null);
    setLoadError(null);
    reset();
    onClose();
  };

  const onSubmit = async (data: { code: string }) => {
    if (!setupToken) return;

    try {
      await completeTwoFactorSetup(setupToken, data.code);
      await refreshAuth();
      handleClose();
      onSuccess();
    } catch (err: any) {
      setError("root", { message: err.message });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-white mb-4">Set Up Two-Factor Authentication</h2>

        {loading && <p className="text-sm text-zinc-400">Preparing setup...</p>}

        {loadError && (
          <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">{loadError}</div>
        )}

        {setupDetails && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">Scan this QR code with Google Authenticator or another TOTP app.</p>

            <div className="rounded bg-white p-4 flex items-center justify-center">
              <img src={setupDetails.qrCodeDataUrl} alt="Two-factor QR code" className="w-full max-w-[180px]" />
            </div>

            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1">Or enter this key manually:</p>
              <div className="rounded border border-zinc-700 bg-zinc-800 px-3 py-2 font-mono text-sm text-zinc-200 break-all select-all">
                {setupDetails.manualEntryKey}
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2 border-t border-zinc-800">
              {errors.root && (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
                  {errors.root.message}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Enter the 6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  {...register("code", {
                    required: "Code is required",
                    pattern: { value: /^\d{6}$/, message: "Enter a valid 6-digit code" },
                  })}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {errors.code && <p className="mt-1 text-sm text-red-400">{errors.code.message}</p>}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Verifying..." : "Enable 2FA"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Two-Factor Disable Dialog ---

type TwoFactorDisableDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const TwoFactorDisableDialog = ({ isOpen, onClose, onSuccess }: TwoFactorDisableDialogProps) => {
  const apiClient = useApiClient();
  const { refreshAuth } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<DisableTwoFactorFormData>({
    defaultValues: { currentPassword: "", code: "" },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: DisableTwoFactorFormData) => {
    try {
      await apiClient.post("/api/auth/2fa/disable", data);
      await refreshAuth();
      handleClose();
      onSuccess();
    } catch (err: any) {
      setError("root", { message: err.message });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-white mb-2">Disable Two-Factor Authentication</h2>
        <p className="text-sm text-zinc-400 mb-4">Confirm your password and enter a current authenticator code.</p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.root && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
              {errors.root.message}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              {...register("currentPassword", { required: "Current password is required" })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.currentPassword && <p className="mt-1 text-sm text-red-400">{errors.currentPassword.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Authenticator code</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="123456"
              {...register("code", {
                required: "Code is required",
                pattern: { value: /^\d{6}$/, message: "Enter a valid 6-digit code" },
              })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.code && <p className="mt-1 text-sm text-red-400">{errors.code.message}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded transition-colors disabled:opacity-50"
            >
              {isSubmitting ? "Disabling..." : "Disable 2FA"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Indy Media Link Dialog ---

type IndyLinkDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (connection: IndyConnection) => void;
};

const IndyLinkDialog = ({ isOpen, onClose, onSuccess }: IndyLinkDialogProps) => {
  const apiClient = useApiClient();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<IndyLinkFormData>({
    defaultValues: { email: "", password: "" },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: IndyLinkFormData) => {
    try {
      const response = await apiClient.post<IndyConnectionResponse>("/api/integrations/indy/connect", data);
      reset();
      onSuccess(response.connection);
      onClose();
    } catch (err: any) {
      setError("root", { message: err.message });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-semibold text-white mb-2">Link Indy Media</h2>
        <p className="text-sm text-zinc-400 mb-4">
          Enter your Indy Media credentials to connect it as a data source for this IndyTV account.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.root && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
              {errors.root.message}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Indy Media email</label>
            <input
              type="email"
              autoComplete="email"
              {...register("email", { required: "Email is required" })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="you@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1">Indy Media password</label>
            <input
              type="password"
              autoComplete="current-password"
              {...register("password", { required: "Password is required" })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {errors.password && <p className="mt-1 text-sm text-red-400">{errors.password.message}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50"
            >
              {isSubmitting ? "Connecting..." : "Link Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Main Settings Screen ---

export default () => {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const [security, setSecurity] = useState<SecurityResponse | null>(null);
  const [indyConnection, setIndyConnection] = useState<IndyConnection | null>(null);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [indyConnectionLoading, setIndyConnectionLoading] = useState(true);
  const [indyConnectionError, setIndyConnectionError] = useState<string | null>(null);
  const [disconnectingIndy, setDisconnectingIndy] = useState(false);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [indyLinkDialogOpen, setIndyLinkDialogOpen] = useState(false);

  const loadSecurity = async () => {
    setSecurityLoading(true);
    try {
      const data = await apiClient.get<SecurityResponse>("/api/auth/security");
      setSecurity(data);
    } catch {
      // Security info is non-critical, silently fail
    } finally {
      setSecurityLoading(false);
    }
  };

  const loadIndyConnection = async () => {
    setIndyConnectionLoading(true);
    setIndyConnectionError(null);
    try {
      const data = await apiClient.get<IndyConnectionResponse>("/api/integrations/indy/connection");
      setIndyConnection(data.connection);
    } catch (err: any) {
      setIndyConnectionError(err.message);
    } finally {
      setIndyConnectionLoading(false);
    }
  };

  const disconnectIndy = async () => {
    setDisconnectingIndy(true);
    setIndyConnectionError(null);
    try {
      await apiClient.delete("/api/integrations/indy/connection");
      setIndyConnection({ connected: false });
    } catch (err: any) {
      setIndyConnectionError(err.message);
    } finally {
      setDisconnectingIndy(false);
    }
  };

  useEffect(() => {
    loadSecurity();
    loadIndyConnection();
  }, []);

  if ((securityLoading && !security) || (indyConnectionLoading && !indyConnection)) {
    return (
      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  const indyTokenExpiry = indyConnection?.connected ? formatExpiry(indyConnection.expiresAt) : null;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-6 pb-4">
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-sm text-zinc-400 mt-1">Manage your account and security</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className="space-y-4">
          {/* Account */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-zinc-700/50 rounded">
                <User size={18} className="text-zinc-400" />
              </div>
              <h2 className="text-sm font-semibold text-white">Account</h2>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-zinc-500">Name</p>
                <p className="text-sm text-white mt-1">
                  {user?.firstName} {user?.lastName}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-zinc-500">Email</p>
                <p className="text-sm text-white mt-1">{user?.email}</p>
              </div>
            </div>

            <div className="mt-5 pt-5 border-t border-zinc-800">
              <button
                onClick={() => setPasswordDialogOpen(true)}
                className="w-full flex items-center gap-2 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors group"
              >
                <LockKeyhole size={14} />
                <span className="flex-1 text-left">Change Password</span>
                <ChevronRight size={14} className="text-zinc-600 group-hover:text-zinc-400 transition-colors" />
              </button>
            </div>
          </div>

          {/* Security */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-zinc-700/50 rounded">
                <ShieldCheck size={18} className="text-zinc-400" />
              </div>
              <h2 className="text-sm font-semibold text-white">Security</h2>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white">Two-Factor Authentication</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {security?.twoFactorEnabled ? "Enabled — using Google Authenticator" : "Not enabled"}
                </p>
              </div>

              {security?.twoFactorEnabled ? (
                <button
                  onClick={() => setDisableDialogOpen(true)}
                  className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/20 hover:bg-red-500/30 rounded transition-colors"
                >
                  Disable
                </button>
              ) : (
                <button
                  onClick={() => setSetupDialogOpen(true)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors"
                >
                  Set up 2FA
                </button>
              )}
            </div>
          </div>

          {/* Integrations */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-zinc-700/50 rounded">
                <Link2 size={18} className="text-zinc-400" />
              </div>
              <h2 className="text-sm font-semibold text-white">Integrations</h2>
            </div>

            {indyConnectionError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
                {indyConnectionError}
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-white">Indy Media</p>
                {indyConnection?.connected ? (
                  <div className="text-xs text-zinc-500 mt-1 space-y-1">
                    <p>
                      Connected as <span className="text-zinc-300">{indyConnection.email ?? "Unknown email"}</span>
                    </p>
                    {indyConnection.clientName && <p>Client: {indyConnection.clientName}</p>}
                    {indyTokenExpiry && <p>Token expires: {indyTokenExpiry}</p>}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500 mt-1">Not linked</p>
                )}
              </div>

              {indyConnection?.connected ? (
                <button
                  onClick={disconnectIndy}
                  disabled={disconnectingIndy}
                  className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/20 hover:bg-red-500/30 rounded transition-colors disabled:opacity-50"
                >
                  {disconnectingIndy ? "Disconnecting..." : "Disconnect"}
                </button>
              ) : (
                <button
                  onClick={() => setIndyLinkDialogOpen(true)}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors"
                >
                  Link Account
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ChangePasswordDialog isOpen={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} />

      <TwoFactorSetupDialog
        isOpen={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSuccess={loadSecurity}
      />

      <TwoFactorDisableDialog
        isOpen={disableDialogOpen}
        onClose={() => setDisableDialogOpen(false)}
        onSuccess={loadSecurity}
      />

      <IndyLinkDialog
        isOpen={indyLinkDialogOpen}
        onClose={() => setIndyLinkDialogOpen(false)}
        onSuccess={setIndyConnection}
      />
    </div>
  );
};
